/**
 * Admin axios instance — separate from buyer client to avoid token cross-pollination.
 * Buyer tokens never enter admin requests and vice versa.
 *
 * Storage keys (intentionally distinct from buyer):
 *   shutterkart_admin_access_token
 *   shutterkart_admin_refresh_token
 *   shutterkart_admin_user           ({id,email,full_name,role})
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const ADMIN_API_BASE = `${BACKEND_URL}/api`;

export const ADMIN_KEYS = {
  access: "shutterkart_admin_access_token",
  refresh: "shutterkart_admin_refresh_token",
  user: "shutterkart_admin_user",
};

function readLS(key) {
  try {
    return typeof window === "undefined" ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLS(key, val) {
  try {
    if (val == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, val);
  } catch {
    /* ignore */
  }
}

export function getAdminAccessToken() {
  return readLS(ADMIN_KEYS.access);
}
export function getAdminRefreshToken() {
  return readLS(ADMIN_KEYS.refresh);
}
export function getAdminUser() {
  const raw = readLS(ADMIN_KEYS.user);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
export function persistAdminAuth({ access_token, refresh_token, user }) {
  if (access_token) writeLS(ADMIN_KEYS.access, access_token);
  if (refresh_token) writeLS(ADMIN_KEYS.refresh, refresh_token);
  if (user) writeLS(ADMIN_KEYS.user, JSON.stringify(user));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("shutterkart-admin-auth-change"));
  }
}
export function clearAdminAuth() {
  writeLS(ADMIN_KEYS.access, null);
  writeLS(ADMIN_KEYS.refresh, null);
  writeLS(ADMIN_KEYS.user, null);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("shutterkart-admin-auth-change"));
  }
}

export const adminApi = axios.create({
  baseURL: ADMIN_API_BASE,
  timeout: 20000,
});

adminApi.interceptors.request.use((cfg) => {
  const t = getAdminAccessToken();
  if (t && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let refreshing = null;
async function tryAdminRefresh() {
  if (refreshing) return refreshing;
  const rt = getAdminRefreshToken();
  if (!rt) return null;
  refreshing = axios
    .post(`${ADMIN_API_BASE}/admin/auth/refresh`, { refresh_token: rt }, { timeout: 15000 })
    .then((r) => {
      persistAdminAuth(r.data);
      return r.data.access_token;
    })
    .catch(() => {
      clearAdminAuth();
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

adminApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status === 401 &&
      !original._retried &&
      !String(original.url || "").includes("/admin/auth/")
    ) {
      original._retried = true;
      const newAccess = await tryAdminRefresh();
      if (newAccess) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return adminApi(original);
      }
    }
    return Promise.reject(error);
  },
);

// ----- Endpoint helpers -----
export const adminLogin = (email, password) =>
  adminApi.post("/admin/auth/login", { email, password }).then((r) => r.data);
export const adminLogout = (refresh_token) =>
  adminApi.post("/admin/auth/logout", { refresh_token }).then((r) => r.data);

export const adminListQuotes = (params = {}) =>
  adminApi.get("/admin/quotes", { params }).then((r) => r.data);
export const adminGetQuote = (id) =>
  adminApi.get(`/admin/quotes/${id}`).then((r) => r.data);
export const adminRespondQuote = (id, body) =>
  adminApi.post(`/admin/quotes/${id}/respond`, body).then((r) => r.data);
export const adminConfirmOffline = (id, body) =>
  adminApi.post(`/admin/quotes/${id}/confirm-offline`, body).then((r) => r.data);
export const adminAddInternalNote = (id, note) =>
  adminApi.post(`/admin/quotes/${id}/internal-note`, { note }).then((r) => r.data);
export const adminListMessages = (id, params = {}) =>
  adminApi.get(`/admin/quotes/${id}/messages`, { params }).then((r) => r.data);
export const adminPostMessage = (id, body, attachments = []) =>
  adminApi.post(`/admin/quotes/${id}/messages`, { body, attachments }).then((r) => r.data);

export const adminListTalent = (params = {}) =>
  adminApi.get("/admin/talent", { params }).then((r) => r.data);
export const adminCreateTalent = (body) =>
  adminApi.post("/admin/talent", body).then((r) => r.data);
export const adminPatchTalent = (id, body) =>
  adminApi.patch(`/admin/talent/${id}`, body).then((r) => r.data);
export const adminDeleteTalent = (id) =>
  adminApi.delete(`/admin/talent/${id}`).then((r) => r.data);

export const adminListUsers = () =>
  adminApi.get("/admin/users").then((r) => r.data);
export const adminCreateUser = (body) =>
  adminApi.post("/admin/users", body).then((r) => r.data);
export const adminPatchUser = (id, body) =>
  adminApi.patch(`/admin/users/${id}`, body).then((r) => r.data);
export const adminResetPassword = (id, new_password) =>
  adminApi.post(`/admin/users/${id}/reset-password`, { new_password }).then((r) => r.data);
export const adminListUserRoles = () =>
  adminApi.get("/admin/user-roles").then((r) => r.data);

// Catalog (re-used in admin "Add from library")
export const adminListPackages = (params = {}) =>
  adminApi.get("/packages", { params }).then((r) => r.data);
export const adminListAddons = () =>
  adminApi.get("/addons").then((r) => r.data);

// ----- Phase 6: CMS -----
export const adminCms = {
  list: (entity, params = {}) =>
    adminApi.get(`/admin/${entity}`, { params }).then((r) => r.data),
  get: (entity, id) =>
    adminApi.get(`/admin/${entity}/${id}`).then((r) => r.data),
  create: (entity, body) =>
    adminApi.post(`/admin/${entity}`, body).then((r) => r.data),
  patch: (entity, id, body) =>
    adminApi.patch(`/admin/${entity}/${id}`, body).then((r) => r.data),
  remove: (entity, id) =>
    adminApi.delete(`/admin/${entity}/${id}`).then((r) => r.data),
};

export const adminGetPricingRules = () =>
  adminApi.get("/admin/pricing-rules").then((r) => r.data);
export const adminUpdatePricingRules = (body) =>
  adminApi.put("/admin/pricing-rules", body).then((r) => r.data);

export const adminListQuestionSets = () =>
  adminApi.get("/admin/occasion-question-sets").then((r) => r.data);
export const adminGetQuestionSet = (occasion) =>
  adminApi.get(`/admin/occasion-question-sets/${occasion}`).then((r) => r.data);
export const adminUpsertQuestionSet = (occasion, body) =>
  adminApi.put(`/admin/occasion-question-sets/${occasion}`, body).then((r) => r.data);

export const adminListCalendar = (params = {}) =>
  adminApi.get("/admin/pricing-calendar", { params }).then((r) => r.data);
export const adminUpsertCalendar = (entries) =>
  adminApi.post("/admin/pricing-calendar", { entries }).then((r) => r.data);
export const adminDeleteCalendarEntry = (date) =>
  adminApi.delete(`/admin/pricing-calendar/${date}`).then((r) => r.data);

export const adminUploadImage = (file, folder = "cms") => {
  const form = new FormData();
  form.append("file", file);
  return adminApi
    .post(`/admin/uploads/image?folder=${encodeURIComponent(folder)}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

// ----- Phase 6: Reports -----
export const adminReportDaily = (params = {}) =>
  adminApi.get("/admin/reports/daily", { params }).then((r) => r.data);
export const adminReportWeekly = (params = {}) =>
  adminApi.get("/admin/reports/weekly", { params }).then((r) => r.data);
export const adminReportMonthly = (params = {}) =>
  adminApi.get("/admin/reports/monthly", { params }).then((r) => r.data);
export function adminReportCsvUrl(kind, params = {}) {
  const qs = new URLSearchParams({ ...params, export: "csv" }).toString();
  return `${ADMIN_API_BASE}/admin/reports/${kind}?${qs}`;
}

// ----- Phase 6: Audit logs -----
export const adminListAuditLogs = (params = {}) =>
  adminApi.get("/admin/audit-logs", { params }).then((r) => r.data);
export const adminListAuditActors = () =>
  adminApi.get("/admin/audit-logs/distinct/actors").then((r) => r.data);
